import { useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChapterText, ContinueButton, Revelation, Whisper } from '../zen-ui';

/** Chapter II — every note is sealed by your key. Touch the note to sign it. */
export function SignNote({ onComplete }: { onComplete: () => void }) {
  const [signed, setSigned] = useState(false);

  return (
    <div className="relative z-10 flex min-h-dvh flex-col items-center justify-center gap-12 px-6 py-24 text-center">
      <ChapterText numeral="II · the signature" title="your voice cannot be forged.">
        Every note you write is sealed by your key. Anyone can verify it — no
        one can fake it.
      </ChapterText>

      <div className="animate-zen-fade-up relative" style={{ animationDelay: '800ms' }}>
        {signed && (
          <div className="pointer-events-none absolute inset-0 animate-zen-ripple rounded-3xl border border-amber-200/60" />
        )}
        <button
          type="button"
          onClick={() => setSigned(true)}
          disabled={signed}
          aria-label="sign the note"
          className={cn(
            'zen-glass relative w-72 rounded-3xl p-8 text-left transition-all duration-1000 sm:w-80',
            signed
              ? 'border-amber-200/40 shadow-[0_0_60px_rgba(246,212,147,0.16)]'
              : 'cursor-pointer hover:border-white/25 hover:shadow-[0_0_40px_rgba(255,255,255,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-100/60',
          )}
        >
          <p className="font-serif text-xl italic leading-relaxed text-slate-200/90">
            “the river is quiet tonight.”
          </p>
          <div className="mt-6 space-y-2" aria-hidden="true">
            <div className="h-1.5 w-3/4 rounded-full bg-white/10" />
            <div className="h-1.5 w-1/2 rounded-full bg-white/10" />
          </div>
          <div className="mt-8 flex items-center justify-between">
            <span
              className={cn(
                'font-mono text-[10px] tracking-[0.25em] transition-colors duration-1000',
                signed ? 'text-amber-200/80' : 'text-white/25',
              )}
            >
              {signed ? 'sig · 8f3a…9c2e' : 'unsigned'}
            </span>
            <span
              className={cn(
                'grid h-9 w-9 place-items-center rounded-full transition-all duration-1000',
                signed
                  ? 'bg-[radial-gradient(circle_at_35%_30%,#fff8e7,#f6d493_55%,#c9963f)] shadow-[0_0_26px_rgba(246,212,147,0.6)]'
                  : 'border border-dashed border-white/25',
              )}
            >
              {signed && <Check className="h-4 w-4 text-[#5b4519]" strokeWidth={2.5} />}
            </span>
          </div>
        </button>
      </div>

      {!signed ? (
        <Whisper delay={1100}>touch the note to sign it</Whisper>
      ) : (
        <div className="flex flex-col items-center gap-8">
          <Revelation delay={300}>
            mathematics vouches for you — not a server, not a company.
          </Revelation>
          <ContinueButton onClick={onComplete} delay={1100} />
        </div>
      )}
    </div>
  );
}
